// src/pages/VerifyEmailChange.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { CheckCircle as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import { contactAPI } from '../services/api';

const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await contactAPI.verifyEmailChange(token);
      setStatus('success');
      setNewEmail(response.data.newEmail);
      setMessage('Your email address has been updated successfully!');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || 'Verification failed. The link may be invalid or expired.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center' }}>
        {status === 'verifying' && (
          <>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email...</Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we confirm your new email address.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Email Updated!
            </Typography>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            {newEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your new email address is: <strong>{newEmail}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can now use this email address to log in to your account.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you need to change your email address, please request a new verification link from your account settings.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/settings')}
            >
              Go to Settings
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyEmailChange;
